import { useGetLmsPlanningInstructorsQuery, useAddLmsPlanningInstructorMutation, useDeleteLmsPlanningInstructorMutation } from '../../../../api';
import toastService from '../../../../utils/toastService';
import { formatErrorMessage } from '../../../../utils/errorUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';

/**
 * Planlama detayındaki eğitmen ataması için veri hook'u.
 * Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 2.md §9.
 *
 * Katalog seviyesindeki `useTrainingInstructorsData` ile aynı desen; fark, atamanın `trainingId`
 * yerine `planningId`'ye yapılmasıdır ve `instructorsCount` `LmsCatalogPlanning`'i değil yalnızca
 * bu planlamanın kendi eğitmen satırlarını etkiler (bkz. lmsPlanningInstructor.js).
 */
const usePlanningInstructorsData = (planningId) => {
    const { th } = useLanguage();

    const {
        data: instructorsApi,
        isLoading,
        isFetching,
        refetch
    } = useGetLmsPlanningInstructorsQuery({ planningId, pageNumber: 1, pageSize: 100 }, { skip: !planningId, refetchOnMountOrArgChange: true });

    const [addMutation, { isLoading: isAdding }] = useAddLmsPlanningInstructorMutation();
    const [deleteMutation, { isLoading: isDeleting }] = useDeleteLmsPlanningInstructorMutation();

    const addInstructor = async (userId) => {
        try {
            await addMutation({ planningId, userId }).unwrap();
            toastService.success(th('trainingPlanning.instructors.addSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            // 409: aynı planlamaya aynı eğitmen ikinci kez atanmaya çalışılıyor
            if (err?.status === 409) {
                toastService.error(formatErrorMessage(err) || th('trainingPlanning.instructors.alreadyAssigned'), th('common.error'));
            } else {
                toastService.error(formatErrorMessage(err) || th('trainingPlanning.instructors.addError'), th('common.error'));
            }
            return false;
        }
    };

    const removeInstructor = async (id) => {
        try {
            await deleteMutation(id).unwrap();
            toastService.success(th('trainingPlanning.instructors.deleteSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('trainingPlanning.instructors.deleteError'), th('common.error'));
            return false;
        }
    };

    return {
        instructorsApi: instructorsApi || { items: [], totalCount: 0 },
        isLoading,
        isFetching,
        isAdding,
        isDeleting,
        addInstructor,
        removeInstructor,
        refetch
    };
};

export default usePlanningInstructorsData;
